/**
 * API status page that verifies connectivity to NeoLMS and WELA services.
 * Allows users to update API keys and recheck connections before proceeding.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { apiService, authService } from '../services/api.service';
import { useAuth } from '../context/AuthContext';

interface ApiStatusState {
  neolms: {
    status: 'checking' | 'success' | 'error';
    message: string;
  };
  wela: {
    status: 'checking' | 'success' | 'error';
    message: string;
  };
}

/**
 * Displays connection results and offers inline API key updates.
 */
const ApiStatusCheck: React.FC = () => {
  const navigate = useNavigate();
  const { apiKeys, updateApiKey } = useAuth();
  const [status, setStatus] = useState<ApiStatusState>({
    neolms: { status: 'checking', message: 'Checking NeoLMS connection...' },
    wela: { status: 'checking', message: 'Checking WELA connection...' },
  });
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [apiKeyValue, setApiKeyValue] = useState('');

  // Check API connections on component mount
  useEffect(() => {
    checkApiConnections();
  }, []);

  const checkApiConnections = async () => {
    setLoading(true);
    
    try {
      // Check NeoLMS connection
      const neolmsResult = await apiService.checkNeolmsConnection();
      setStatus(prev => ({
        ...prev,
        neolms: {
          status: neolmsResult.status === 'success' ? 'success' : 'error',
          message: neolmsResult.message
        }
      }));
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        neolms: {
          status: 'error',
          message: 'Failed to connect to NeoLMS API. Please check your API key.'
        }
      }));
    }
    
    try {
      // Check WELA connection
      const welaResult = await apiService.checkWelaConnection();
      setStatus(prev => ({
        ...prev,
        wela: {
          status: welaResult.status === 'success' ? 'success' : 'error',
          message: welaResult.message
        }
      }));
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        wela: {
          status: 'error',
          message: 'Failed to connect to WELA API. Please check your API token.'
        }
      }));
    }
    
    setLoading(false);
  };

  const handleContinue = () => {
    // Only proceed if both APIs are connected successfully
    if (status.neolms.status === 'success' && status.wela.status === 'success') {
      navigate('/select-school-year');
    } else {
      // Show error if trying to continue with failed connections
      alert('Please fix API connection issues before continuing.');
    }
  };

  const handleOpenDialog = (apiType: string) => {
    setOpenDialog(apiType);
    // Set initial value based on current API key
    const currentKey = apiKeys.find(k => k.service_name === apiType.toLowerCase())?.api_key || '';
    setApiKeyValue(currentKey);
  };

  const handleCloseDialog = () => {
    setOpenDialog(null);
    setApiKeyValue('');
  };

  const handleUpdateApiKey = async () => {
    if (!openDialog || !apiKeyValue.trim()) return;
    
    try {
      await updateApiKey(openDialog.toLowerCase(), apiKeyValue);
      handleCloseDialog();
      // Recheck connections after updating key
      checkApiConnections();
    } catch (error) {
      console.error('Failed to update API key:', error);
      alert('Failed to update API key. Please try again.');
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            API Connection Status
          </Typography>
          
          <Typography variant="body1" paragraph>
            Before proceeding, we need to verify connections to both the NeoLMS and WELA APIs.
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 3 }}>
              <Alert 
                severity={status.neolms.status === 'success' ? 'success' : 'error'}
                action={
                  <Button 
                    color="inherit" 
                    size="small"
                    onClick={() => handleOpenDialog('neolms')}
                  >
                    Update Key
                  </Button>
                }
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle1">NeoLMS API:</Typography>
                {status.neolms.message}
              </Alert>
              
              <Alert 
                severity={status.wela.status === 'success' ? 'success' : 'error'}
                action={
                  <Button 
                    color="inherit" 
                    size="small"
                    onClick={() => handleOpenDialog('wela')}
                  >
                    Update Key
                  </Button>
                }
                sx={{ mb: 3 }}
              >
                <Typography variant="subtitle1">WELA API:</Typography>
                {status.wela.message}
              </Alert>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button 
                  variant="outlined" 
                  onClick={checkApiConnections}
                >
                  Recheck Connections
                </Button>
                
                <Button 
                  variant="contained" 
                  onClick={handleContinue}
                  disabled={status.neolms.status !== 'success' || status.wela.status !== 'success'}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
      
      {/* API Key Update Dialog */}
      <Dialog open={!!openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          Update {openDialog === 'neolms' ? 'NeoLMS API Key' : 'WELA API Token'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter your {openDialog === 'neolms' ? 'NeoLMS API Key' : 'WELA API Token'} below:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={openDialog === 'neolms' ? 'API Key' : 'API Token'}
            type="text"
            fullWidth
            variant="outlined"
            value={apiKeyValue}
            onChange={(e) => setApiKeyValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleUpdateApiKey} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApiStatusCheck;